import React from 'react';

import { FlexColumn } from '@editor/components/utilities/layouts';
import { PAYMENT_FAILED } from '@editor/global/analytics/events';
import analytics from '@repo/analytics';
import { Button, secondaryTheme } from '@repo/ui/components/Buttons';
import { Spacer } from '@repo/ui/components/Spacer';
import { H4, P3 } from '@repo/ui/components/Typography';
import { useNewThemeActive, useTheme } from '@repo/ui/theming';
import { buildWebsiteUrl } from '@repo/utils/url';

import {
  paymentStoreSelector,
  usePaymentStore,
} from '../../stores/paymentStore';
import RemoteDataStatus from '../../types/RemoteDataStatus';

import {
  failedPaymentErrorData,
  prepaidCardNotAllowedErrorData,
} from './errors';

interface Parameters {
  // For now this is only used for subscription, but it might be reused for POD.
  type: 'subscription';
}

const PaymentError: React.FC<Parameters> = (props: Parameters) => {
  const { type } = props;

  const paymentTransactionError = usePaymentStore(
    paymentStoreSelector.transactionError
  );
  const paymentErrorKind = usePaymentStore(paymentStoreSelector.errorKind);
  const paymentStatus = usePaymentStore(paymentStoreSelector.status);
  const paymentActions = usePaymentStore(paymentStoreSelector.actions);

  const isNewThemeActive = useNewThemeActive();
  const { isDarkTheme } = useTheme();

  if (paymentStatus !== RemoteDataStatus.Error) {
    return null;
  }

  const handleTryAgainClick = (): void => {
    if (paymentErrorKind === 'loadStripe') {
      void paymentActions.init();
    } else {
      paymentActions.reset();
    }
  };

  const errorCode = paymentTransactionError?.code ?? 'generic_error';

  const data =
    paymentErrorKind === 'prepaidCardNotAllowed'
      ? prepaidCardNotAllowedErrorData
      : failedPaymentErrorData(errorCode);

  if (isNewThemeActive) {
    let src = isDarkTheme
      ? '/images/newTheme/dark/Failed.png'
      : '/images/newTheme/light/Failed.png';
    if (data.isBlocked) {
      src = isDarkTheme
        ? '/images/newTheme/dark/Payment blocked.png'
        : '/images/newTheme/light/Payment blocked.png';
    }

    data.image = <img src={src} style={{ height: '300px' }} />;
  }

  void analytics.track(PAYMENT_FAILED, {
    error: paymentErrorKind,
    errorCode,
    type,
  });

  return (
    <FlexColumn
      alignItems="center"
      justifyContent="center"
      style={{ textAlign: 'center', marginLeft: '10%', marginRight: '10%' }}
    >
      {data.image}
      <Spacer height="24px" />
      <H4>{data.title}</H4>
      <Spacer height="12px" />
      <P3>{data.description}</P3>
      <Spacer height="24px" />
      <FlexColumn alignItems="center" gap="10px">
        <Button
          label={data.retryLabel}
          name="retryPaymentErrorButton"
          width="220px"
          onClick={handleTryAgainClick}
        />
        {data.supportUrl && (
          <Button
            as="a"
            disabled={false}
            href={buildWebsiteUrl(data.supportUrl)}
            label="Get help"
            name="paymentErrorGetHelpButton"
            target="_blank"
            theme={secondaryTheme}
            width="220px"
          />
        )}
      </FlexColumn>
    </FlexColumn>
  );
};

export default PaymentError;
